import { BoltonStatus, useRewardsQuery } from '@/src/api/use-rewards-query';
import {
  ACTIVE_REWARDS_CONTENT,
  REMAINING_DEFAULT_CONTENT_WHEN_SOME_REWARDS_ACTIVE,
  ACTIVATED_USERS_WITHOUT_REWARDS_DEFAULT_CONTENT,
  NON_ACTIVATED_USERS_DEFAULT_CONTENT,
} from '../content';
import { FunctionComponent, useMemo } from 'react';
import { useBeyondAccountStatusQuery } from '@/src/api/use-beyond-account-status';
import { BeyondAccountStatus } from '@/src/types/Response';
import { useRewardsFeature } from './useRewardsFeature';

type RewardContent = {
  Icon: FunctionComponent<React.SVGProps<SVGSVGElement>>;
  title: string;
  text: string;
};

const TOTAL_NO_OF_REWARDS_TO_SHOW = 4;

export const useRewardsCardsContent = () => {
  const { data: beyondAccountStatusData } = useBeyondAccountStatusQuery();
  const beyondAccountStatus =
    beyondAccountStatusData?.beyondAccountStatus?.status;

  const { data: rewardsData } = useRewardsQuery();
  const { enabled } = useRewardsFeature();

  const cardsContent = useMemo(() => {
    if (!enabled || !beyondAccountStatus) {
      return [];
    }

    if (beyondAccountStatus === BeyondAccountStatus.Activated) {
      const rewardsContent: RewardContent[] = [];

      ACTIVE_REWARDS_CONTENT.forEach(content => {
        const { productId, Icon, title } = content;

        const isRewardActive = rewardsData?.find(
          reward =>
            reward.productId === productId &&
            reward.status === BoltonStatus.ACTIVE,
        );

        if (isRewardActive) {
          rewardsContent.push({
            Icon,
            title,
            text: content.text(
              isRewardActive.currentLevel,
              isRewardActive.maxLevel,
            ),
          });
        }
      });

      const remainingRewardsToShow =
        TOTAL_NO_OF_REWARDS_TO_SHOW - rewardsContent.length;

      if (rewardsContent.length === 0) {
        return ACTIVATED_USERS_WITHOUT_REWARDS_DEFAULT_CONTENT;
      }

      if (remainingRewardsToShow > 0) {
        rewardsContent.push(
          ...REMAINING_DEFAULT_CONTENT_WHEN_SOME_REWARDS_ACTIVE.slice(
            0,
            remainingRewardsToShow,
          ),
        );
      }

      return rewardsContent;
    }

    return NON_ACTIVATED_USERS_DEFAULT_CONTENT;
  }, [beyondAccountStatus, enabled, rewardsData]);

  return {
    cardsContent,
  };
};
